import { AlertTriangle, ShieldAlert } from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import { SectionCard, InfoRow } from '../shared';

/**
 * The seller-facing "Danger zone" (close / re-open / delete) maps to admin
 * moderation. Close, ban, transfer and delete live on the Moderation tab with
 * suspend/publish/review; this section surfaces the lifecycle state and points
 * there so the actions are not duplicated (and audited) in two places.
 */
export const DangerSection: React.FC<{ store: AdminStore; onGoToModeration?: () => void }> = ({
  store,
  onGoToModeration,
}) => (
  <div className="space-y-4">
    <SectionCard title="Lifecycle state">
      <div className="divide-y divide-gray-100">
        <InfoRow label="Suspended">{store.suspended === 1 ? `Yes${store.suspend_reason ? ` — ${store.suspend_reason}` : ''}` : 'No'}</InfoRow>
        <InfoRow label="In review">{store.review === 1 ? 'Yes' : 'No'}</InfoRow>
        <InfoRow label="Published">{store.published === 1 ? 'Yes' : 'No'}</InfoRow>
        <InfoRow label="Admin-closed">{store.admin_closed === 1 ? `Yes${store.admin_close_reason ? ` — ${store.admin_close_reason}` : ''}` : 'No'}</InfoRow>
        <InfoRow label="Banned">{store.banned === 1 ? `Yes${store.ban_reason ? ` — ${store.ban_reason}` : ''}` : 'No'}</InfoRow>
      </div>
    </SectionCard>

    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
        <div>
          <p className="text-sm font-semibold text-amber-900">Moderation actions live on the Moderation tab</p>
          <p className="mt-0.5 text-sm text-amber-700">
            Force-close, re-open, ban, transfer and delete — along with suspend, publish, review and verify —
            are performed there so every action is captured with a reason in the audit log.
          </p>
          {onGoToModeration && (
            <button
              type="button"
              onClick={onGoToModeration}
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700"
            >
              <AlertTriangle className="h-4 w-4" /> Go to Moderation
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
);
